import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X } from "lucide-react";

type Platform = "ios" | "android" | "other";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function InstallAppButton() {
  const [platform, setPlatform] = useState<Platform>("other");
  const [installed, setInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setInstalled(isStandalone());

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  if (platform === "other") return null;

  const handleClick = async () => {
    if (platform === "ios") {
      setShowIosHelp(true);
      return;
    }
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
    } else {
      setShowIosHelp(true);
    }
  };

  return (
    <>
      <div className="md:hidden mt-6 flex justify-center">
        <Button
          variant="outline"
          size="lg"
          onClick={handleClick}
          className="text-base px-6 h-12 gap-2"
        >
          <Download className="h-5 w-5" />
          {platform === "ios" ? "Add to Home Screen" : "Install App"}
        </Button>
      </div>

      {showIosHelp && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosHelp(false)}
        >
          <div
            className="bg-background rounded-2xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIosHelp(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {platform === "ios" ? "Install on iOS" : "Install on Android"}
            </h3>
            {platform === "ios" ? (
              <ol className="space-y-3 text-sm text-foreground">
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-primary">1.</span>
                  <span className="flex items-center gap-1.5">
                    Tap the Share button <Share className="h-4 w-4 inline" /> in Safari
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-primary">2.</span>
                  <span className="flex items-center gap-1.5">
                    Scroll and tap <strong>Add to Home Screen</strong> <Plus className="h-4 w-4 inline" />
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-primary">3.</span>
                  <span>
                    Tap <strong>Add</strong> in the top-right corner
                  </span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-foreground">
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-primary">1.</span>
                  <span>Open the browser menu (⋮)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-primary">2.</span>
                  <span>
                    Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-primary">3.</span>
                  <span>
                    Confirm by tapping <strong>Install</strong>
                  </span>
                </li>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
}
